import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Image,
  Progress,
  Tooltip,
  Drawer,
  Descriptions,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
  EyeOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { ConfigVersionSelect } from '@/components/ConfigVersionSelect';
import dayjs from 'dayjs';
import {
  BrandCampaign,
  BrandCampaignStatus,
  BrandCampaignSpots,
  BRAND_CAMPAIGN_SPOT_KEYS,
} from '@/types';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_TAG_COLOR: Record<BrandCampaignStatus, string> = {
  draft: 'default',
  scheduled: 'blue',
  active: 'green',
  paused: 'orange',
  ended: 'red',
};

const STATUS_OPTIONS: { label: string; value: BrandCampaignStatus }[] = [
  { label: 'Draft', value: 'draft' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Active', value: 'active' },
  { label: 'Paused', value: 'paused' },
  { label: 'Ended', value: 'ended' },
];

// Group the 17 spots into UI sections so admins can scan / fill them in a
// predictable order. Pure presentation — the wire payload remains a flat
// object keyed by the spot names.
const SPOT_GROUPS: ReadonlyArray<{
  title: string;
  keys: ReadonlyArray<keyof BrandCampaignSpots>;
}> = [
  {
    title: 'In-stadium banners',
    keys: [
      'boundaryBanner',
      'behindStumps',
      'outfieldLongBanner',
      'outfieldSmallBanner',
      'sightscreen',
    ],
  },
  {
    title: 'Match overlays',
    keys: ['awardBoard', 'umpireBoard', 'tossScreen', 'actionReplay'],
  },
  {
    title: 'Selection screens',
    keys: ['batSelection', 'bowlSelection'],
  },
  {
    title: 'Scorecards & charts',
    keys: ['battingScorecard', 'bowlingScorecard', 'chartPanel', 'targetScreen'],
  },
  {
    title: 'Menus',
    keys: ['landingTile', 'menuSponsorLogo'],
  },
];

const SPOT_LABEL: Record<keyof BrandCampaignSpots, string> = {
  boundaryBanner: 'Boundary banner',
  behindStumps: 'Behind stumps',
  outfieldLongBanner: 'Outfield long banner',
  outfieldSmallBanner: 'Outfield small banner',
  sightscreen: 'Sightscreen',
  awardBoard: 'Award board',
  umpireBoard: 'Umpire board',
  tossScreen: 'Toss screen',
  actionReplay: 'Action replay',
  batSelection: 'Bat selection',
  bowlSelection: 'Bowl selection',
  battingScorecard: 'Batting scorecard',
  bowlingScorecard: 'Bowling scorecard',
  chartPanel: 'Chart panel',
  targetScreen: 'Target screen',
  landingTile: 'Landing tile',
  menuSponsorLogo: 'Menu sponsor logo',
};

interface FormValues {
  name: string;
  status: BrandCampaignStatus;
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
  spots?: Record<string, string | undefined>;
  configVersionId?: string | null;
}

const HTTPS_URL_REGEX = /^https:\/\/[^\s]+$/i;

const spotsFilledCount = (spots: BrandCampaignSpots | undefined): number => {
  if (!spots) return 0;
  return BRAND_CAMPAIGN_SPOT_KEYS.filter((k) => {
    const v = spots[k];
    return typeof v === 'string' && v.length > 0;
  }).length;
};

const BrandingPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<BrandCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<BrandCampaignStatus | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDrawerId, setShowDrawerId] = useState<string | null>(null);
  const [form] = Form.useForm<FormValues>();
  const [version, setVersion] = useState<string | null>(null);
  const [versionSegment, setVersionSegment] = useState<'major' | 'minor' | 'patch'>('patch');
  const [adjustingDirection, setAdjustingDirection] = useState<'up' | 'down' | null>(null);
  const [versionInput, setVersionInput] = useState('');
  const [settingVersion, setSettingVersion] = useState(false);

  const fetchVersion = useCallback(async () => {
    try {
      const res = await adminApi.getBrandingVersion();
      setVersion(res.data?.version ?? null);
    } catch (error) {
      console.error('Failed to fetch branding version', error);
      setVersion(null);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        includeEnded: true,
        limit: 100,
      };
      if (statusFilter) params.status = statusFilter;
      const res = await adminApi.getBrandCampaigns(params);
      const data = res.data?.data || res.data?.items || res.data;
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch brand campaigns', error);
      message.error('Failed to load brand campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
    void fetchVersion();
  }, [fetchData, fetchVersion]);

  const handleAdjustVersion = async (direction: 'up' | 'down') => {
    setAdjustingDirection(direction);
    try {
      const res = await adminApi.bumpBrandingVersion({
        segment: versionSegment,
        direction,
      });
      const next = res.data?.version;
      setVersion(next ?? null);
      message.success(
        `Branding version ${direction === 'up' ? 'bumped' : 'taken down'} (${versionSegment}) to ${next}`
      );
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string | string[] } } };
      const detail = err.response?.data?.message;
      message.error(
        Array.isArray(detail)
          ? detail.join('; ')
          : detail || `Failed to ${direction === 'up' ? 'bump' : 'take down'} version`
      );
    } finally {
      setAdjustingDirection(null);
    }
  };

  const handleSetVersion = async () => {
    const trimmed = versionInput.trim();
    if (!trimmed) {
      message.warning('Enter a version string first');
      return;
    }
    setSettingVersion(true);
    try {
      const res = await adminApi.setBrandingVersion(trimmed);
      const next = res.data?.version;
      setVersion(next ?? null);
      setVersionInput('');
      message.success(`Branding version set to ${next}`);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string | string[] } } };
      const detail = err.response?.data?.message;
      message.error(Array.isArray(detail) ? detail.join('; ') : detail || 'Failed to set version');
    } finally {
      setSettingVersion(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'draft',
    });
    setModalOpen(true);
  };

  const openEdit = (row: BrandCampaign) => {
    setEditingId(row.id);
    form.setFieldsValue({
      name: row.name,
      status: row.status,
      dateRange: [dayjs(row.startsAt), dayjs(row.endsAt)],
      spots: row.spots as Record<string, string | undefined>,
      configVersionId: row.configVersionId ?? undefined,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      // Drop any empty-string spot values so the JSONB column stays clean.
      const cleanSpots: Record<string, string> = {};
      if (values.spots) {
        for (const k of BRAND_CAMPAIGN_SPOT_KEYS) {
          const v = values.spots[k];
          if (typeof v === 'string' && v.trim().length > 0) {
            cleanSpots[k] = v.trim();
          }
        }
      }

      const payload: Record<string, unknown> = {
        name: values.name,
        status: values.status,
        startsAt: values.dateRange[0].toISOString(),
        endsAt: values.dateRange[1].toISOString(),
        spots: cleanSpots,
        // Explicit payload, so this has to be listed or the picker silently
        // does nothing. null (not undefined) clears the scope back to shared.
        configVersionId: values.configVersionId ?? null,
      };

      if (editingId) {
        await adminApi.updateBrandCampaign(editingId, payload);
        message.success('Brand campaign updated');
      } else {
        await adminApi.createBrandCampaign(payload);
        message.success('Brand campaign created');
      }

      setModalOpen(false);
      form.resetFields();
      void fetchData();
      // Server auto-bumps brandingConfigVersion on every write — refresh
      // the displayed value so admins see clients will pick up the change.
      void fetchVersion();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string | string[] } } };
      const detail = err.response?.data?.message;
      message.error(Array.isArray(detail) ? detail.join('; ') : detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminApi.deleteBrandCampaign(id);
      message.success('Brand campaign deleted');
      void fetchData();
      void fetchVersion();
    } catch (error) {
      console.error(error);
      message.error('Delete failed');
    }
  };

  const columns: ColumnsType<BrandCampaign> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (v: string) => <Text strong>{v}</Text>,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (v: BrandCampaignStatus) => (
          <Tag color={STATUS_TAG_COLOR[v]}>{v.toUpperCase()}</Tag>
        ),
      },
      {
        title: 'Window',
        key: 'window',
        render: (_: unknown, row: BrandCampaign) => (
          <Text type="secondary">
            {dayjs(row.startsAt).format('MMM D, HH:mm')} →{' '}
            {dayjs(row.endsAt).format('MMM D, HH:mm')}
          </Text>
        ),
      },
      {
        title: 'Spots filled',
        key: 'spots',
        width: 160,
        render: (_: unknown, row: BrandCampaign) => {
          const filled = spotsFilledCount(row.spots);
          const percent = Math.round((filled / 17) * 100);
          return (
            <Tooltip title={`${filled} of 17 image slots populated`}>
              <Progress percent={percent} size="small" format={() => `${filled}/17`} />
            </Tooltip>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        render: (_: unknown, row: BrandCampaign) => (
          <Space>
            <Button size="small" icon={<EyeOutlined />} onClick={() => setShowDrawerId(row.id)}>
              View
            </Button>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
              Edit
            </Button>
            <Popconfirm
              title="Delete this campaign?"
              description="This is permanent. Set status to Ended instead if you want to preserve history."
              onConfirm={() => handleDelete(row.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const drawerCampaign = useMemo(
    () => campaigns.find((c) => c.id === showDrawerId) || null,
    [campaigns, showDrawerId]
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <PictureOutlined /> Branding
            </Title>
            <Text type="secondary">
              Brand-collab campaign manifest served at <code>GET /v1/branding/active-campaign</code>
              . Only campaigns with status ACTIVE within the start/end window ship to clients.
            </Text>
            <div style={{ marginTop: 8 }}>
              <Space size="small" wrap>
                <Tag color="geekblue">
                  Config version: <strong>{version ?? '—'}</strong>
                </Tag>
                <Select
                  size="small"
                  style={{ width: 110 }}
                  value={versionSegment}
                  onChange={(v) => setVersionSegment(v)}
                  options={[
                    { label: 'Major', value: 'major' },
                    { label: 'Minor', value: 'minor' },
                    { label: 'Patch', value: 'patch' },
                  ]}
                />
                <Tooltip
                  title={`+1 on ${versionSegment}. Bumping major/minor resets lower segments to 0.`}
                >
                  <Button
                    size="small"
                    type="primary"
                    icon={<ArrowUpOutlined />}
                    loading={adjustingDirection === 'up'}
                    disabled={adjustingDirection === 'down'}
                    onClick={() => handleAdjustVersion('up')}
                  >
                    Bump
                  </Button>
                </Tooltip>
                <Tooltip
                  title={`−1 on ${versionSegment}. Lower segments unchanged. Goes 400 if it would go below 0.`}
                >
                  <Button
                    size="small"
                    danger
                    icon={<ArrowDownOutlined />}
                    loading={adjustingDirection === 'down'}
                    disabled={adjustingDirection === 'up'}
                    onClick={() => handleAdjustVersion('down')}
                  >
                    Take down
                  </Button>
                </Tooltip>
                <Input.Group compact>
                  <Input
                    size="small"
                    placeholder="e.g. 1.2.0"
                    style={{ width: 140 }}
                    value={versionInput}
                    onChange={(e) => setVersionInput(e.target.value)}
                    onPressEnter={handleSetVersion}
                    maxLength={50}
                    allowClear
                  />
                  <Tooltip title="Pin the version to this exact string. Use for hand-aligning with a specific build's expected value. Auto-bump from this point continues as semver patch (so 1.2.0 → 1.2.1 next time).">
                    <Button
                      size="small"
                      loading={settingVersion}
                      onClick={handleSetVersion}
                      disabled={versionInput.trim().length === 0}
                    >
                      Set
                    </Button>
                  </Tooltip>
                </Input.Group>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Unity reads this from <code>/v1/config</code> and skips the branding fetch when
                  unchanged.
                </Text>
              </Space>
            </div>
          </Col>
          <Col>
            <Space>
              <Select
                allowClear
                placeholder="Filter by status"
                style={{ width: 180 }}
                value={statusFilter}
                onChange={(v) => setStatusFilter(v)}
                options={STATUS_OPTIONS}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                New campaign
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={campaigns}
          columns={columns}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        open={modalOpen}
        title={editingId ? 'Edit campaign' : 'New campaign'}
        width={820}
        confirmLoading={submitting}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText={editingId ? 'Save' : 'Create'}
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Name" rules={[{ required: true, max: 200 }]}>
                <Input placeholder="Pepsi IPL 2026" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="configVersionId"
            label="Config Version"
            extra="Leave empty to run this campaign on every build. Pick a config version to limit it to the app versions mapped to that version."
          >
            <ConfigVersionSelect style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Active window (UTC)"
            rules={[
              { required: true, message: 'Pick start and end' },
              {
                validator: (_rule, value: [dayjs.Dayjs, dayjs.Dayjs] | undefined) =>
                  value && value[1].isAfter(value[0])
                    ? Promise.resolve()
                    : Promise.reject(new Error('End must be after start')),
              },
            ]}
          >
            <RangePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>

          <Title level={5} style={{ marginTop: 16 }}>
            Image slots
          </Title>
          <Text type="secondary">
            All URLs must be HTTPS — the Unity player rejects insecure connections. Leave a field
            blank to ship no asset for that slot.
          </Text>

          {SPOT_GROUPS.map((group) => (
            <Card key={group.title} size="small" title={group.title} style={{ marginTop: 12 }}>
              <Row gutter={16}>
                {group.keys.map((k) => (
                  <Col span={12} key={k}>
                    <Form.Item
                      name={['spots', k]}
                      label={SPOT_LABEL[k]}
                      rules={[
                        {
                          validator: (_rule, value: string | undefined) => {
                            if (!value || value.trim().length === 0) {
                              return Promise.resolve();
                            }
                            return HTTPS_URL_REGEX.test(value.trim())
                              ? Promise.resolve()
                              : Promise.reject(new Error('Must be an https:// URL'));
                          },
                        },
                      ]}
                    >
                      <Input placeholder="https://cdn.../image.png" />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Card>
          ))}
        </Form>
      </Modal>

      <Drawer
        title={drawerCampaign?.name || 'Campaign'}
        width={520}
        open={showDrawerId != null}
        onClose={() => setShowDrawerId(null)}
      >
        {drawerCampaign && (
          <>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="ID">
                <Text copyable code style={{ fontSize: 12 }}>
                  {drawerCampaign.id}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_TAG_COLOR[drawerCampaign.status]}>
                  {drawerCampaign.status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Start">
                {dayjs(drawerCampaign.startsAt).format('YYYY-MM-DD HH:mm [UTC]')}
              </Descriptions.Item>
              <Descriptions.Item label="End">
                {dayjs(drawerCampaign.endsAt).format('YYYY-MM-DD HH:mm [UTC]')}
              </Descriptions.Item>
              <Descriptions.Item label="Spots filled">
                {spotsFilledCount(drawerCampaign.spots)} / 17
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginTop: 16 }}>
              Image previews
            </Title>
            {BRAND_CAMPAIGN_SPOT_KEYS.map((k) => {
              const url = drawerCampaign.spots?.[k];
              if (!url) return null;
              return (
                <div key={k} style={{ marginBottom: 12 }}>
                  <Text strong>{SPOT_LABEL[k]}</Text>
                  <div style={{ marginTop: 4 }}>
                    <Image src={url} width={160} fallback="" />
                  </div>
                  <Text type="secondary" copyable style={{ fontSize: 11, wordBreak: 'break-all' }}>
                    {url}
                  </Text>
                </div>
              );
            })}
            {spotsFilledCount(drawerCampaign.spots) === 0 && (
              <Text type="secondary">No spot URLs configured.</Text>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default BrandingPage;
